<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { format, parseISO } from "date-fns";
import GSheetTable from './components/GSheetTable.vue';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import CreateGSheetModal from './components/CreateGSheetModal.vue';
import GSheetConfig from './components/GSheetConfig.vue';
import EmbeddingConfig from './components/EmbeddingConfig.vue';

export default {
  name: "GSheetKBDetails",
  components: {
    GSheetTable,
    CreateGSheetModal,
    GSheetConfig,
    EmbeddingConfig
  },
  data() {
    return {
      isDetailsLoading: false,
      isRowsLoading: false,
      isSyncing: false,
      detailsError: null,
      rowsError: null,
      syncError: null,
      searchQuery: '',
      debouncedSearch: null,
      showModal: false,
      activeIndex: 0,
    };
  },
  computed: {
    ...mapState('gsheet', ['gsheetKBDetails', 'rows']),
    ...mapGetters('gsheet', ['getActiveFilters']),
    allColumns() {
      if (!this.rows?.schema) return '';
      return this.rows.schema.columns.map(col => col.name).join(',');
    },
    activeFilters() {
      return this.getActiveFilters;
    },
    tabs(){
      return [
        { label: 'Data', value: 'data' },
        { label: 'Column Config', value: 'columnConfig' },
        { label: 'Embedding Config', value: 'embeddingConfig' },
      ]
    },
  },
  watch: {
    searchQuery(newVal) {
      this.debouncedSearch(newVal);
    },
    getActiveFilters: {
      handler(newFilters) {
        // Keep search query in sync with filters
        const globalSearchFilter = newFilters.find(f => f.type === 'search' && !f.column);
        if (globalSearchFilter) {
          this.searchQuery = globalSearchFilter.value;
        } else if (!newFilters.some(f => f.type === 'search')) {
          this.searchQuery = '';
        }
      },
      immediate: true
    }
  },
  created() {
    // bind `this` inside debounce
    this.debouncedSearch = debounce((newVal) => {
      if (newVal) {
        this.addFilter({
          type: 'search',
          value: newVal,
          column: null, // Global search across all columns
          label: this.getFilterLabel({ type: 'search', value: newVal })
        });
      } else {
        // Remove any existing search filters
        const searchFilterIndex = this.getActiveFilters.findIndex(f => f.type === 'search' && !f.column);
        if (searchFilterIndex !== -1) {
          this.removeFilter(searchFilterIndex);
        }
      }
      this.refreshRows(this.buildQueryParams());
    }, 400);
  },
  methods: {
    ...mapActions('gsheet', ['fetchGSheetKB', 'fetchRows', 'syncGSheetKB', 'fetchColumns']),
    ...mapMutations('gsheet', ['addFilter', 'removeFilter', 'clearFilters']),
    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        return format(parseISO(dateStr), "dd MMM yyyy, hh:mm a");
      } catch (e) {
        return dateStr;
      }
    },
    clearSearchQuery() {
      this.searchQuery = '';
    },
    getFilterLabel({ type, column, value }) {
      switch (type) {
        case 'search':
          return `Search: "${value}"`;
        case 'sort':
          return `Sort: ${column} (${value})`;
        case 'status':
          return value ? 'Enabled' : 'Disabled';
        case 'gte':
          return `${column} ≥ ${value}`;
        case 'lte':
          return `${column} ≤ ${value}`;
        case 'gt':
          return `${column} > ${value}`;
        case 'lt':
          return `${column} < ${value}`;
        default:
          return `${type}: ${value}`;
      }
    },
    applyFilter({ column, action, value }) {
      if (value !== null && value !== undefined) {
        // Create the filter object
        const filter = {
          type: action,
          column: column?.name,
          value,
          label: this.getFilterLabel({ type: action, column: column?.name, value })
        };
        
        // Add to Vuex store
        this.addFilter(filter);
      }

      // Call API with all active filters
      const params = this.buildQueryParams();
      this.refreshRows(params);
    },
    buildQueryParams() {
      const params = { page: 1 }; // Reset to first page when filtering

      this.getActiveFilters.forEach(filter => {
        switch (filter.type) {
          case 'search':
            params.q = filter.value;
            params.search_options = filter.column || this.allColumns;
            break;
          case 'sort':
            params.sort_by = filter.column;
            params.sort_order = filter.value;
            break;
          case 'status':
            params.enabled = filter.value;
            break;
          case 'gte':
          case 'lte':
          case 'gt':
          case 'lt':
            params.q = `${filter.type}:${filter.value}`;
            params.search_options = filter.column;
            break;
        }
      });

      return params;
    },
    clearFilter(index) {
      this.removeFilter(index);
      const params = this.buildQueryParams();
      this.refreshRows(params);
    },
    clearAllFilters() {
      this.clearFilters();
      this.searchQuery = '';
      this.refreshRows();
    },
    async handleColumnAction({ column, action, value }) {
      try {
        this.applyFilter({ column, action, value });
      } catch (err) {
        console.error("Failed to handle column action:", err);
      }
    },
    async refreshDetails() {
      this.isDetailsLoading = true;
      this.detailsError = null;
      try {
        await this.fetchGSheetKB(this.$route.params.GSheetKBId);
      } catch (err) {
        this.detailsError = Array.isArray(err)
          ? err.join('\n')
          : 'Failed to fetch knowledge base details';
        console.error(err);
      } finally {
        this.isDetailsLoading = false;
      }
    },
    async refreshRows({
      page = 1,
      limit = 20,
      enabled = null,
      search_options = null,
      q = null,
      sort_by = null,
      sort_order = 'asc'
    } = {}) {
      this.isRowsLoading = true;
      this.rowsError = null;
      try {
        await this.fetchRows({
          knowledgeId: this.$route.params.GSheetKBId,
          page,
          limit,
          enabled,
          search_options,
          q,
          sort_by,
          sort_order,
        });
      } catch (err) {
        this.rowsError = Array.isArray(err)
          ? err.join('\n')
          : 'Failed to fetch knowledge base rows';
        console.error(err);
      } finally {
        this.isRowsLoading = false;
      }
    },
    async refreshColumns() {
      try {
        this.isError = false;
        this.isLoading = true;
        await this.fetchColumns(this.$route.params.GSheetKBId);
      } catch (err) {
        console.error(err);
        this.isError = true;
        useAlert('Error fetching columns:', err);
      } finally {
        this.isLoading = false;
      }
    },
    handlePageChange(newPage) {
      this.refreshRows({ page: newPage, limit: this.rows.limit });
    },
    handlePageSizeChange(newSize) {
      this.refreshRows({ page: 1, limit: newSize });
    },
    async sync() {
      try {
        useAlert('Syncing...');
        this.isSyncing = true;
        this.syncError = null;
        await this.syncGSheetKB(this.$route.params.GSheetKBId);
        await this.refreshDetails();
        await this.refreshRows();
      } catch (err) {
        this.syncError = Array.isArray(err)
          ? err.join('\n')
          : 'Failed to sync';
        console.error(err);
      } finally {
        this.isSyncing = false;
      }
    },
    onTabChange(index) {
      this.activeIndex = index
    },
  },
  mounted() {
    this.refreshDetails();
    this.refreshRows();
    this.refreshColumns();
  }
};
</script>

<template>
  <div class="p-2 md:p-8 flex flex-1 h-full gap-3 flex-col m-0 bg-slate-25 dark:bg-slate-900 overflow-auto">
    <!-- Modals -->
    <CreateGSheetModal
      v-if="showModal"
      :is-edit-mode="true"
      :edit-obj="gsheetKBDetails"
      @close="showModal = false"
      @refresh="refreshDetails"
    />

    <!-- Header -->
    <div class="flex w-full justify-between mb-4">
      <div class="flex gap-2 items-center">
        <woot-button variant="clear" icon="chevron-left" @click="$router.back()"/>
        <p class="text-2xl font-bold mb-0 text-slate-900 dark:text-slate-100">
          Google Sheet Knowledge Base Details
        </p>
      </div>
    </div>

    <!-- KB Details Section -->
    <div class="bg-white dark:bg-slate-800 shadow rounded-2xl p-6 space-y-3 border border-slate-200 dark:border-slate-700">
      <!-- Loading State -->
      <div v-if="isDetailsLoading" class="flex items-center justify-center py-8 text-slate-600 dark:text-slate-300">
        <woot-spinner size="medium" />
        <span class="ml-2">Loading knowledge base details...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="detailsError" class="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
        <div class="flex items-center text-red-700 dark:text-red-400">
          <woot-icon name="alert" size="16" class="mr-2" />
          {{ detailsError }}
        </div>
        <woot-button
          variant="link"
          size="small"
          class="mt-2 text-red-700 dark:text-red-400"
          @click="refreshDetails"
        >
          Try Again
        </woot-button>
      </div>

      <!-- Content -->
      <template v-else>
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div class="flex flex-wrap gap-4 items-end mb-2">
              <h2 class="text-lg md:text-2xl font-bold break-words text-slate-900 dark:text-slate-100">
                {{ gsheetKBDetails.name || 'Untitled Knowledge Base' }}
              </h2>
              <p
                :class="{
                  'text-green-600 dark:text-green-400': gsheetKBDetails.sync_status === 'completed',
                  'text-yellow-600 dark:text-yellow-400': gsheetKBDetails.sync_status === 'idle',
                  'text-red-600 dark:text-red-400': gsheetKBDetails.sync_status === 'failed'
                }"
                class="text-sm font-medium mb-0"
              >
                {{ gsheetKBDetails.sync_status.toUpperCase() }}
              </p>
              <p class="text-sm text-slate-700 dark:text-slate-300 mb-0">
                <span class="font-medium">Last Synced:</span>
                {{ formatDate(gsheetKBDetails.last_synced_at) }}
              </p>
            </div>
            <p class="text-sm text-slate-400 dark:text-slate-500 break-all mb-0">
              {{ gsheetKBDetails.id }}
            </p>

            <!-- Description -->
            <p class="text-sm text-slate-600 dark:text-slate-300 mt-2">
              {{ gsheetKBDetails.description || '-' }}
            </p>
          </div>

          <div class="flex flex-wrap gap-2 md:gap-3">
            <woot-button
              color-scheme="secondary"
              size="small"
              icon="edit"
              @click="showModal = true"
              class="hover:bg-slate-700 dark:hover:bg-slate-500 w-full md:w-auto"
              v-tooltip.top="'Edit Description'"
            />
            <a @click.stop :href="gsheetKBDetails.gsheet_url" target="_blank">
              <woot-button
                icon="open"
                size="small"
                class="hover:bg-woot-700 dark:hover:bg-woot-500 w-full md:w-auto"
                v-tooltip.top="'Open Google Sheet'"
              />
            </a>
            <woot-button
              variant="primary"
              size="small"
              icon="arrow-rotate-clockwise"
              :is-loading="isSyncing"
              @click="sync"
              class="hover:bg-woot-700 dark:hover:bg-woot-500 w-full md:w-auto"
              v-tooltip.top="'Sync'"
            />
          </div>
        </div>

        <div v-if="gsheetKBDetails.sync_error_message" class="text-sm space-y-2">
          <span class="font-medium text-slate-900 dark:text-slate-100">Error:</span>
          <span class="text-red-600 dark:text-red-400 break-words">
            {{ gsheetKBDetails.sync_error_message }}
          </span>
        </div>
      </template>
    </div>

    <!-- Tabs -->
    <woot-tabs
      :index="activeIndex"
      class="-mt-px bg-white dashboard-app--tabs dark:bg-slate-900"
      @change="onTabChange"
    >
      <woot-tabs-item
        v-for="tab in tabs"
        :key="tab.label"
        :name="tab.label"
        :show-badge="false"
      />
    </woot-tabs>

    <div v-if="tabs[activeIndex].value === 'data'">
      <!-- Search query section -->
      <div class="relative max-w-md">
        <div v-if="!searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10 pointer-events-none">
          <fluent-icon icon="search" class="h-5 w-5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
        </div>
        <button
          type="button"
          v-if="searchQuery"
          @click="clearSearchQuery"
          class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10"
        >
          <fluent-icon icon="dismiss" class="h-5 w-5 text-red-400 hover:text-red-600 dark:text-red-300 dark:hover:text-red-400" />
        </button>
        <input
          type="text"
          class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg py-2 pl-10 pr-10 w-full focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
          :placeholder="`Search in all columns...`"
          v-model="searchQuery"
        />
      </div>

      <!-- Active Filters Section -->
      <div v-if="activeFilters.length > 0" class="flex flex-wrap gap-2 items-center mb-4">
        <div
          v-for="(filter, index) in activeFilters"
          :key="`${filter.type}-${index}`"
          class="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full text-sm"
        >
          <span class="text-slate-700 dark:text-slate-300">{{ filter.label }}</span>
          <button
            @click="clearFilter(index)"
            class="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <fluent-icon icon="dismiss" class="h-4 w-4" />
          </button>
        </div>

        <button
          @click="clearAllFilters"
          class="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
        >
          Clear All
        </button>
      </div>

      <!-- KB Rows Section -->
      <div class="bg-white dark:bg-slate-800 shadow rounded-2xl border border-slate-200 dark:border-slate-700">
        <div v-if="isRowsLoading" class="flex items-center justify-center py-8 text-slate-600 dark:text-slate-300">
          <woot-spinner size="medium" />
          <span class="ml-2">Loading rows...</span>
        </div>

        <div v-else-if="rowsError" class="p-4">
          <div class="flex items-center text-red-700 dark:text-red-400">
            <woot-icon name="alert" size="16" class="mr-2" />
            {{ rowsError }}
          </div>
          <woot-button
            variant="link"
            size="small"
            class="mt-2 text-red-700 dark:text-red-400"
            @click="refreshRows"
          >
            Try Again
          </woot-button>
        </div>

        <template v-else>
          <GSheetTable
            :rows="rows"
            @action="handleColumnAction"
            @page-change="handlePageChange"
            @page-size-change="handlePageSizeChange"
            @row-updated="refreshRows({ page: rows.page, limit: rows.limit })"
          />
        </template>
      </div>
    </div>

    <div v-else-if="tabs[activeIndex].value === 'columnConfig'">
      <GSheetConfig @refresh="refreshColumns" />
    </div>

    <div v-else-if="tabs[activeIndex].value === 'embeddingConfig'">
      <EmbeddingConfig />
    </div>
  </div>
</template>
